






































import {
    FormField,
    FormStylesFormInputs,
    FormStylesFormTitle
} from '@/types/Form.interface'
import { InputType } from '@/types/Input.interface'
import Vue, { PropType } from 'vue'
export default Vue.extend({
    props: {
        formFields: {
            type: Array as PropType<FormField[]>,
            required: true
        },
        formStyles: {
            type: Object,
            required: true
        },
        submitted: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            //  The height of the primary content
            contentHeight: window.innerHeight,
            pageHeight: '100%',
            //  A temporary form model so we can simulate editing form fields
            temp: {},
            //  Our link for referrals
            referralLink: `${process.env.VUE_APP_URL_SITE}/?utm_source=messagedesk&utm_medium=anonymousForm&utm_campaign=formReferral`
        }
    },
    mounted() {
        //  Load our extensions
        this.loadExtensions()

        //  Calculate the height only on next tick
        this.$nextTick(() => {
            //  Calculate the current content height for initial state
            this.calculateContentHeight()

            //  Add this event listener so we recalculate the height whenever window is resized
            window.addEventListener('resize', this.calculateContentHeight)
        })
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.calculateContentHeight)
    },
    computed: {
        //  Our inputs need to be formatted and styled for the display
        dynamicInputs(): FormField[] {
            //  Grab our style columns
            const styleColumns = this.formInputStyles.columns

            //  Our cols depend on the style columns
            const cols =
                {
                    1: 12,
                    2: 6,
                    3: 4
                }[styleColumns] || 12

            //  Iterate through our fields to adjust them for our preview
            this.formFields.forEach((input: FormField) => {
                //  Modify our input to add validation
                const formatted = this.formatInputs(input)

                //  Apply our styles to our formatted input
                const styled = this.styleInputs(formatted)

                //  Update our current input
                input = styled
            })

            //  If our cols are NOT 4 or 6 then return our inputs
            if (cols !== 4 && cols !== 6) return this.formFields

            //  Adjust our columns
            this.adjustColumns(cols)

            //  Return our temp form now that it has been modified
            return this.formFields
        },
        formInputStyles(): FormStylesFormInputs {
            //  Grab our form from our styles
            const formStyle = this.formStyles.form

            //  If it doesn't exist then return null
            if (!formStyle) return null

            //  Else, return our inputs
            return formStyle.inputs
        },
        imageSrcForm(): string {
            //  First check if we have an image attached
            const img = this.formStyles?.form?.image

            //  If no image, return our chooseOrganization
            if (!img) return this.$images.chooseOrganization

            //  Else, check if we have a source
            if (img.src) return img.src

            //  Else, check if we want to use default
            if (!img.useDefault) return null

            //  Else, we somehow ended up here so return our chooseOrganization
            return this.$images.chooseOrganization
        },
        imageSrcSuccess(): string {
            //  First check if we have an image attached
            const img = this.formStyles?.success?.image

            //  If no image, return our useSupport
            if (!img) return this.$images.useSupport

            //  Else, check if we have a source
            if (img.src) return img.src

            //  Else, check if we want to use default
            if (!img.useDefault) return null

            //  Else, we somehow ended up here so return our useSupport
            return this.$images.useSupport
        },
        logoHeight(): number {
            //  Grab our breakpoint
            const bp = this.$vuetify.breakpoint.name

            //  Return a different result based on name
            return (
                {
                    sm: 70,
                    xs: 60
                }[bp] || 80
            )
        },
        stylesButton(): object {
            //  Grab our button from the form style
            const btn = this.formStyles.form?.button

            //  If the button doesn't exist, return null
            if (!btn) return null

            //  Else, return our button styles as an object
            return {
                buttonText: btn.value,
                buttonTextColor: btn['text-color'],
                buttonColor: btn.color,
                buttonBorderRadius: btn['border-radius']
            }
        },
        stylesContactForm(): object {
            //  Our page height depends on our current size of our window
            const height = { height: this.pageHeight }

            //  Grab our inner page from our styles
            const inner = this.formStyles.page?.inner

            //  If this doesn't exist, then just return our height
            if (!inner) return height

            //  Else, construct our styles
            return {
                'background-color': inner['background-color'],
                'background-image': `url(${inner['background-image']})`,
                ...height
            }
        },
        stylesFormTitle(): object {
            //  Grab our title
            const title = this.title

            //  If it doesn't exist, return null
            if (!title) return null

            //  Else, define our styles
            return {
                color: title.color,
                'letter-spacing': `${title['letter-spacing']}px`,
                'font-size': `${title.size}px`
            }
        },
        stylesSuccessSubtitle(): object {
            //  Grab our success msg from the styles
            const msg = this.formStyles.success?.message

            //  If nothing then return
            if (!msg) return null

            //  Else, construct our styles
            return {
                color: msg.color,
                'letter-spacing': `${msg['letter-spacing']}px`,
                'font-size': `${msg.size}px`
            }
        },
        stylesSuccessTitle(): object {
            //  Grab our success title
            const title = this.formStyles.success?.title

            //  If nothing then return
            if (!title) return null

            //  Else, construct our styles
            return {
                color: title.color,
                'letter-spacing': `${title['letter-spacing']}px`,
                'font-size': `${title.size}px`
            }
        },
        //  Our title belonging to the form styles
        title(): FormStylesFormTitle {
            //  Grab our styles form form
            const formStyle = this.formStyles.form

            //  If it doesn't exist, return null
            if (!formStyle) return null

            //  Else, grab our title from it
            return formStyle.title
        }
    },
    methods: {
        //  Adjusts our column spacing on our form fields
        adjustColumns(cols: number): void {
            //  Grab the number of fields we have
            const n = this.formFields.length

            //  Modulo 3 our inputs to see if we use 1/3 or 2/3 of our column space
            const mod3 = n % 3

            //  Modulo 2 our inputs to see if we are even or odd (1 for odd 0 for even)
            const mod2 = n % 2

            //  If we have 4 columns AND we use 1/3 of our column space OR we have 6 columns and we are odd
            const need12 = (cols === 4 && mod3 === 1) || (cols === 6 && mod2 === 1)

            //  If we have 4 columns AND we use 2/3 of our columns space
            const need6 = cols === 4 && mod3 === 2

            //  If neither of these apply, then do nothing
            if (!need12 && !need6) return

            //  Else, adjust our columns
            if (need12) this.formFields[n - 1].cols = 12
            else if (need6) {
                this.formFields[n - 1].cols = 6
                this.formFields[n - 2].cols = 6
            }
        },
        //  The height of the primary content
        calculateContentHeight(): number {
            //  Get the height of the view
            let body = document.body.clientHeight

            //  Define the toolbars offset
            let toolbars = 70

            //  define the padding offset (accounts for padding on smAndUp)
            let padding = 25

            //  Define the app bottom navbar offset
            let navbar = 15

            //  Subtract the height of our app bar, toolbars, navbar, and padding
            let height = body - (toolbars + padding + navbar)

            //  Return height in pixels
            this.contentHeight = height

            return height
        },
        //  Formats a specific input to have validation applied
        formatInputs(input: FormField): FormField {
            //  Our required field is applied if the input validation matches it
            const required = input.required && !input.validate.includes('|required')

            //  Grab the input type as a lowercase field
            const type = input.type.toLowerCase()

            //  Check if we need to modify the input type
            const modify = type === 'number' || type === 'phone'

            //  Our numeric field is required if the input is a number
            const number = !input.validate.includes('|numeric')

            //  Our phone field is required if the input is a phone
            const phone = !input.validate.includes('|phone')

            //  If we are required, add the required to our validate
            if (required) input.validate.concat('|required')
            //  If we need to modify then apply our input type
            if (modify) input.type = InputType.TEXT
            //  If we are a number field then add our validation
            if (number) input.validate.concat('|numeric')
            //  If we are a phone field then add our validation
            if (phone) input.validate.concat('|phone')

            //  Return the formatted input
            return input
        },
        //  Loads our extension values into each input on our temporary form we are modifying
        loadExtensions(): void {
            this.formFields.forEach((field: FormField) => {
                //  Grab our path and type
                const path = field.path
                const type = field.type

                //  If our field path IS in temp then continue
                if (path in this.temp) return

                //  Else, we need to update if this is a DATE field
                if (type === 'DATE' || type === 'DATETIME') {
                    this.temp[path] = new Date().toISOString()
                }
                //  Else, it is just null
                else this.temp[path] = null
            })
        },
        //  Styles an input with styles from our formInputStyles prop
        styleInputs(input: FormField, cols: number): FormField {
            //  Grab our props from our input styles
            const props = this.formInputStyles.props

            //  If for some reason these props do not exist, just return our field
            if (!props) return input

            //  Map these props to our input
            input['background-color'] = props['background-color']
            input.color = props.color
            input.cols = cols
            input.colStyle = 'padding:0 3px !important;'
            input.dark = props.dark
            input.dense = props.dense
            input.filled = props.filled
            input.height = props.height
            input.outlined = props.outlined
            input.rounded = props.rounded
            input.shaped = props.shaped

            //  Return the updated input
            return input
        }
    },
    watch: {
        contentHeight(val: number): void {
            //  Our height is contentHeight
            const height = val

            this.pageHeight = `${height - 74}px`
        }
    }
})
